import { useEffect, useState } from "react";
import { useFirestoreCollection } from "../hooks/useFirestoreCollection";
import type { IncidentEntry } from "../lib/types";

const SEVERITY_VOICED = {
  alarm: "Alarm",
  actie: "Actie",
  "let-op": "Let op",
  info: "Info",
} as const;

/**
 * Off-screen aria-live region that announces newly added incidents to
 * assistive tech. Polite for info/let-op, assertive for actie/alarm.
 *
 * Renders nothing visible — accessibility-only.
 */
export function LiveAnnouncer() {
  const { data: incidents } = useFirestoreCollection<IncidentEntry>(
    "incidents",
    { orderField: "time", orderDir: "desc" },
  );
  const [politeMsg, setPoliteMsg] = useState("");
  const [assertiveMsg, setAssertiveMsg] = useState("");
  const [seen, setSeen] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (incidents.length === 0) return;
    const newOnes = incidents.filter((i) => !seen.has(i.id));
    if (newOnes.length === 0) return;
    const latest = newOnes[0];
    const txt = `${SEVERITY_VOICED[latest.severity]} bij ${latest.location}: ${latest.title}`;
    if (latest.severity === "alarm" || latest.severity === "actie") {
      setAssertiveMsg(txt);
    } else {
      setPoliteMsg(txt);
    }
    setSeen(new Set(incidents.map((i) => i.id)));
  }, [incidents, seen]);

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeMsg}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveMsg}
      </div>
    </>
  );
}
